import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import clsx from "clsx";
import { useEffect, useState } from "react";

import Clickable from "./Clickable.jsx";
import generateUniqueId from "./generateUniqueId.js";
import handleComponentsProp from "./handleComponentsProp.js";
import Icon from "./Icon.jsx";
import isPropValid from "./isPropValid.js";
import useThemeProps from "./useThemeProps.jsx";

const DropdownNavRoot = styled("nav")`
  color: ${({ theme }) => theme.getColor("white.link")};
`;

const DropdownNavLabel = styled("div", {
  shouldForwardProp: isPropValid,
})``;

const DropdownNavLabelToggle = styled(Clickable, {
  shouldForwardProp: isPropValid,
})`
  ${({ theme }) => theme.styleUtils.buttonReset}
  display: flex;
  width: 100%;
  align-items: center;
  cursor: pointer;
  gap: ${({ theme }) => theme.getLength([2, 4])};
`;

const DropdownNavLabelToggleIcon = styled(Icon, {
  shouldForwardProp: isPropValid,
})`
  margin-inline-start: auto;
  flex: none;
`;

const DropdownNavLabelContent = styled("span", {
  shouldForwardProp: isPropValid,
})``;

const DropdownNavContent = styled("div", {
  shouldForwardProp: isPropValid,
})``;

function DropdownNavList({ children, ...restProps }) {
  return (
    <ol
      css={css`
        list-style: none;
        padding: 0;
        margin: 0;
      `}
      {...restProps}
    >
      {children}
    </ol>
  );
}

const DropdownNavItem = styled(Clickable, {
  shouldForwardProp: isPropValid,
})`
  ${({ theme }) => theme.styleUtils.buttonReset}
  display: block;
  width: 100%;
  background: ${({ theme }) => theme.getColor("white")};
`;

export default function DropdownNav(props) {
  // eslint-disable-next-line no-unused-vars
  const theme = useTheme();
  props = useThemeProps({ props, name: "DropdownNav" });
  let {
    className,
    label,
    items,
    id,
    isInitiallyExpanded = false,
    isExpanded,
    spacing = 4,
    onToggle = () => {
      setIsStatefullyExpanded(!isStatefullyExpanded);
    },
    components,
    printable = false,
    ...restProps
  } = props;

  let defaultComponents = {
    Label: DropdownNavLabel,
    LabelToggle: DropdownNavLabelToggle,
    LabelToggleIcon: DropdownNavLabelToggleIcon,
    LabelContent: DropdownNavLabelContent,
    Content: DropdownNavContent,
    List: DropdownNavList,
    Item: DropdownNavItem,
  };

  components = handleComponentsProp(components, defaultComponents);
  let {
    Label,
    LabelToggle,
    LabelToggleIcon,
    LabelContent,
    Content,
    List,
    Item,
  } = components;

  let [isStatefullyExpanded, setIsStatefullyExpanded] =
    useState(isInitiallyExpanded);

  isExpanded = isExpanded ?? isStatefullyExpanded;

  let [defaultId] = useState(`dropdown-nav-${generateUniqueId()}`);

  id = id ?? defaultId;

  let ownerState = {
    label,
    id,
    isInitiallyExpanded,
    onToggle,
    defaultComponents,
    components,
    isExpanded,
    spacing,
    ...restProps,
  };

  let [rootElement, setRootElement] = useState(null);

  useEffect(() => {
    if (!rootElement) {
      return;
    }
    let listener = (event) => {
      if (
        rootElement !== event.target &&
        !rootElement.contains(event.target) &&
        isExpanded
      ) {
        setIsStatefullyExpanded(false);
      }
    };
    document.addEventListener("click", listener);
    return () => {
      document.removeEventListener("click", listener);
    };
  }, [rootElement]);

  return (
    <DropdownNavRoot
      className={clsx(className, "wsui-dropdown-nav", {
        "wsui-is-expanded": isExpanded,
      })}
      ownerState={ownerState}
      id={id}
      css={css`
        position: relative;
        border-radius: 0.5rem;
        border: 1px solid ${theme.getColor("white.link")};
        padding-block: ${theme.getLength([4, 4])};
        padding-inline: ${theme.getLength([4, 6])};
        background: ${theme.getColor("white")};
        &.wsui-is-expanded {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
          border-bottom-color: transparent;
        }
      `}
      ref={(el) => setRootElement(el)}
      {...restProps}
    >
      <Label ownerState={ownerState}>
        <LabelToggle
          className={clsx("wsui-dropdown-nav-label-toggle", {})}
          onClick={(event) => {
            onToggle(isExpanded, event);
          }}
          aria-controls={`${id}-content`}
          aria-expanded={isExpanded}
          ownerState={ownerState}
        >
          <LabelContent id={`${id}-label-content`} ownerState={ownerState}>
            {label}
          </LabelContent>
          <LabelToggleIcon
            ownerState={ownerState}
            name={isExpanded ? "chevron-up" : "chevron-down"}
            aria-hidden={true}
          />
        </LabelToggle>
      </Label>
      <Content
        id={`${id}-content`}
        aria-labelledby={`${id}-label-content`}
        hidden={!isExpanded && !printable}
        css={css`
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          border-radius: 0.5rem;
          margin: -1px;
          border: 1px solid ${theme.getColor("white.link")};
          border-top-left-radius: 0;
          border-top-right-radius: 0;
          border-top-color: transparent;
          overflow: hidden;

          ${printable &&
          !isExpanded &&
          css`
            @media not print {
              display: none;
            }
          `}
        `}
        ownerState={ownerState}
      >
        {isExpanded || printable ? (
          <List>
            {items.map((item, index) => {
              return (
                <li
                  key={index}
                  onClick={() => {
                    setIsStatefullyExpanded(false);
                  }}
                >
                  <Item item={item} />
                </li>
              );
            })}
          </List>
        ) : null}
      </Content>
    </DropdownNavRoot>
  );
}
